import { ExtendDirection } from '@/modules/common';
import { formatDateRange } from '@/utils/date-formatters';

import {
  TeamMemberChangeDeletionConflictData,
  TeamMemberChangeExtensionOption,
} from '../../types/team-member-change-deletion';
import { TeamMemberChangeLogEntry } from '../../types/team-member-changes';

export const findPreviousChange = (
  changes: TeamMemberChangeLogEntry[],
  currentChange: TeamMemberChangeLogEntry
): TeamMemberChangeLogEntry | null => {
  const currentIndex = changes.findIndex(c => c.id === currentChange.id);
  if (currentIndex === -1) return null;

  for (let i = currentIndex + 1; i < changes.length; i++) {
    if (changes[i].type === currentChange.type) {
      return changes[i];
    }
  }
  return null;
};

export const findNextChange = (
  changes: TeamMemberChangeLogEntry[],
  currentChange: TeamMemberChangeLogEntry
): TeamMemberChangeLogEntry | null => {
  const currentIndex = changes.findIndex(c => c.id === currentChange.id);
  if (currentIndex === -1) return null;

  for (let i = currentIndex - 1; i >= 0; i--) {
    if (changes[i].type === currentChange.type) {
      return changes[i];
    }
  }
  return null;
};

const calculateExtendedRange = (
  direction: ExtendDirection,
  adjacentChange: TeamMemberChangeLogEntry,
  deletedChange: TeamMemberChangeLogEntry
): { startDate: string; endDate: string | null } => {
  if (direction === ExtendDirection.Previous) {
    return {
      startDate: adjacentChange.startDate,
      endDate: deletedChange.endDate,
    };
  }

  return {
    startDate: deletedChange.startDate,
    endDate: adjacentChange.endDate,
  };
};

export const buildExtensionOptions = (
  t: (key: string) => string,
  previousChange: TeamMemberChangeLogEntry | null,
  nextChange: TeamMemberChangeLogEntry | null,
  deletedChange: TeamMemberChangeLogEntry
): TeamMemberChangeExtensionOption[] => {
  const options: TeamMemberChangeExtensionOption[] = [];

  if (previousChange) {
    const range = calculateExtendedRange(ExtendDirection.Previous, previousChange, deletedChange);
    options.push({
      direction: ExtendDirection.Previous,
      label: t('authenticated.team:details.tabs.changes.deleteModal.extendPrevious'),
      dateRange: formatDateRange(range.startDate, range.endDate),
      startDate: range.startDate,
      endDate: range.endDate,
    });
  }

  if (nextChange) {
    const range = calculateExtendedRange(ExtendDirection.Next, nextChange, deletedChange);
    options.push({
      direction: ExtendDirection.Next,
      label: t('authenticated.team:details.tabs.changes.deleteModal.shiftNext'),
      dateRange: formatDateRange(range.startDate, range.endDate),
      startDate: range.startDate,
      endDate: range.endDate,
    });
  }

  return options;
};

export const analyzeChangeDeletion = (
  changes: TeamMemberChangeLogEntry[],
  changeToDelete: TeamMemberChangeLogEntry,
  t: (key: string) => string
): TeamMemberChangeDeletionConflictData | null => {
  const previousChange = findPreviousChange(changes, changeToDelete);
  const nextChange = findNextChange(changes, changeToDelete);

  const availableOptions = buildExtensionOptions(t, previousChange, nextChange, changeToDelete);

  if (availableOptions.length <= 1) {
    return null;
  }

  return {
    changeToDelete,
    previousChange,
    nextChange,
    availableOptions,
  };
};
